import { useEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import {
  Tags,
  ChevronRight,
  ChevronDown,
  Search,
  Loader2,
  Eye,
  Layers,
  Lightbulb,
  BarChart3,
  Sparkles,
  ArrowRight,
  ExternalLink,
  Filter,
  Hash,
  ChevronsUpDown,
  Check,
  X,
  User,
  Target,
  Palette,
  Brain,
  ShieldCheck,
} from 'lucide-react';
import { useTaxonomyStore } from '@/stores/taxonomy';
import type { TaxonomyNode, TaxonomyClient, ReferenceAd, CreativeFormula, LinkedTaxonomyNode } from '@/services/api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PERF_COLORS: Record<string, string> = {
  top_1_percent: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  top_5_percent: 'bg-teal-100 text-teal-700 border-teal-200',
  top_10_percent: 'bg-sky-100 text-sky-700 border-sky-200',
  top_25_percent: 'bg-amber-100 text-amber-700 border-amber-200',
  average: 'bg-slate-100 text-slate-600 border-slate-200',
  below_average: 'bg-red-100 text-red-600 border-red-200',
};

function PerfBadge({ tier }: { tier?: string }) {
  if (!tier) return null;
  const label = tier.replace(/_/g, ' ').replace(/percent/, '%');
  return (
    <span
      className={clsx(
        'text-[10px] font-mono px-1.5 py-0.5 rounded border',
        PERF_COLORS[tier] || PERF_COLORS.average,
      )}
    >
      {label}
    </span>
  );
}

function ConfidenceBar({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={clsx(
            'h-full rounded-full transition-all',
            pct >= 80
              ? 'bg-emerald-500'
              : pct >= 50
                ? 'bg-amber-500'
                : 'bg-red-400',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-[10px] font-mono text-slate-500 w-8 text-right">
        {pct}%
      </span>
    </div>
  );
}

function formatCategoryLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Taxonomy Tree Panel (Left)
// ---------------------------------------------------------------------------

function TreeCategory({
  label,
  nodes,
  dimension,
  selectedNode,
  onSelect,
}: {
  label: string;
  nodes: TaxonomyNode[];
  dimension: 'form' | 'meaning';
  selectedNode: TaxonomyNode | null;
  onSelect: (node: TaxonomyNode) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const dimColor = dimension === 'form' ? 'text-indigo-500' : 'text-teal-500';

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 rounded-lg transition-colors group"
      >
        {expanded ? (
          <ChevronDown size={14} className="text-slate-400" />
        ) : (
          <ChevronRight size={14} className="text-slate-400" />
        )}
        <span className="text-sm font-medium text-navy-900 flex-1 text-left truncate">
          {formatCategoryLabel(label)}
        </span>
        <span
          className={clsx(
            'text-[10px] font-mono px-1.5 py-0.5 rounded-full',
            dimension === 'form'
              ? 'bg-indigo-50 text-indigo-600'
              : 'bg-teal-50 text-teal-600',
          )}
        >
          {nodes.length}
        </span>
      </button>
      {expanded && (
        <ul className="ml-5 border-l border-slate-100 pl-2 space-y-0.5 pb-1">
          {nodes.map((node) => (
            <li key={node.node_id}>
              <button
                onClick={() => onSelect(node)}
                className={clsx(
                  'w-full text-left px-2.5 py-1.5 rounded-md text-xs transition-all truncate',
                  selectedNode?.node_id === node.node_id
                    ? 'bg-brand-teal/10 text-brand-teal-dark font-medium border border-brand-teal/20'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-navy-900',
                )}
              >
                {node.display_name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TaxonomyTree() {
  const {
    taxonomyData,
    selectedNode,
    dimensionFilter,
    searchQuery,
    selectNode,
    setDimensionFilter,
    setSearchQuery,
  } = useTaxonomyStore();

  const dims = taxonomyData?.taxonomy_dimensions;
  if (!dims) return null;

  // Filter nodes by search
  const filterNodes = (nodes: TaxonomyNode[]) => {
    if (!searchQuery) return nodes;
    const q = searchQuery.toLowerCase();
    return nodes.filter(
      (n) =>
        n.display_name.toLowerCase().includes(q) ||
        n.canonical_name.toLowerCase().includes(q) ||
        n.description?.toLowerCase().includes(q),
    );
  };

  const formCategories =
    dimensionFilter !== 'meaning'
      ? Object.entries(dims.form)
          .map(([k, v]) => [k, filterNodes(v)] as const)
          .filter(([, v]) => v.length > 0)
      : [];

  const meaningCategories =
    dimensionFilter !== 'form'
      ? Object.entries(dims.meaning)
          .map(([k, v]) => [k, filterNodes(v)] as const)
          .filter(([, v]) => v.length > 0)
      : [];

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="px-3 pt-3 pb-2">
        <div className="relative">
          <Search
            size={14}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="text"
            placeholder="Search nodes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal"
          />
        </div>
      </div>

      {/* Dimension filter */}
      <div className="px-3 pb-2 flex gap-1">
        {(['all', 'form', 'meaning'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setDimensionFilter(f)}
            className={clsx(
              'flex-1 px-2 py-1 rounded-md text-[10px] font-medium transition-all border',
              dimensionFilter === f
                ? 'bg-brand-teal/10 border-brand-teal/30 text-brand-teal-dark'
                : 'border-slate-200 text-slate-500 hover:border-slate-300',
            )}
          >
            {f === 'all' ? 'All' : f === 'form' ? 'Form' : 'Meaning'}
          </button>
        ))}
      </div>

      {/* Tree */}
      <div className="flex-1 overflow-y-auto px-1 pb-3 space-y-0.5">
        {formCategories.length > 0 && (
          <div>
            <h4 className="px-3 pt-2 pb-1 text-[10px] font-mono uppercase text-indigo-500 font-bold tracking-wider flex items-center gap-1.5">
              <Layers size={10} />
              Form
            </h4>
            {formCategories.map(([cat, nodes]) => (
              <TreeCategory
                key={`form-${cat}`}
                label={cat}
                nodes={nodes}
                dimension="form"
                selectedNode={selectedNode}
                onSelect={selectNode}
              />
            ))}
          </div>
        )}

        {meaningCategories.length > 0 && (
          <div>
            <h4 className="px-3 pt-3 pb-1 text-[10px] font-mono uppercase text-teal-500 font-bold tracking-wider flex items-center gap-1.5">
              <Lightbulb size={10} />
              Meaning
            </h4>
            {meaningCategories.map(([cat, nodes]) => (
              <TreeCategory
                key={`meaning-${cat}`}
                label={cat}
                nodes={nodes}
                dimension="meaning"
                selectedNode={selectedNode}
                onSelect={selectNode}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Node Detail Panel (Center)
// ---------------------------------------------------------------------------

function NodeDetail() {
  const { selectedNode, taxonomyData } = useTaxonomyStore();

  if (!selectedNode) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400">
        <div className="text-center">
          <Tags size={32} className="mx-auto mb-3 text-slate-300" />
          <p className="text-sm">Select a taxonomy node</p>
          <p className="text-xs text-slate-400 mt-1">
            Browse the tree to inspect element details
          </p>
        </div>
      </div>
    );
  }

  const node = selectedNode;

  // Find which dimension & category this node belongs to
  let nodeDimension: 'form' | 'meaning' = 'form';
  let nodeCategory = '';
  if (taxonomyData?.taxonomy_dimensions) {
    for (const [cat, nodes] of Object.entries(
      taxonomyData.taxonomy_dimensions.meaning,
    )) {
      if (nodes.some((n) => n.node_id === node.node_id)) {
        nodeDimension = 'meaning';
        nodeCategory = cat;
        break;
      }
    }
    if (!nodeCategory) {
      for (const [cat, nodes] of Object.entries(
        taxonomyData.taxonomy_dimensions.form,
      )) {
        if (nodes.some((n) => n.node_id === node.node_id)) {
          nodeCategory = cat;
          break;
        }
      }
    }
  }

  // Find formulas that reference this node
  const relatedFormulas = taxonomyData?.creative_formulas?.filter(
    (f) =>
      f.hook === node.canonical_name ||
      f.angle === node.canonical_name ||
      f.proof === node.canonical_name ||
      f.cta === node.canonical_name,
  ) || [];

  return (
    <div className="p-5 space-y-5 overflow-y-auto h-full">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span
            className={clsx(
              'text-[10px] font-mono px-1.5 py-0.5 rounded border',
              nodeDimension === 'form'
                ? 'bg-indigo-50 text-indigo-600 border-indigo-200'
                : 'bg-teal-50 text-teal-600 border-teal-200',
            )}
          >
            {nodeDimension}
          </span>
          {nodeCategory && (
            <span className="text-[10px] text-slate-400 font-mono">
              {formatCategoryLabel(nodeCategory)}
            </span>
          )}
        </div>
        <h3 className="text-lg font-bold text-navy-900">
          {node.display_name}
        </h3>
        <p className="text-[11px] font-mono text-slate-400 mt-0.5">
          {node.node_id}
        </p>
      </div>

      {/* Description */}
      {node.description && (
        <div className="bg-slate-50 rounded-lg border border-slate-100 p-3">
          <p className="text-sm text-slate-700 leading-relaxed">
            {node.description}
          </p>
        </div>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <p className="text-[10px] font-mono text-slate-500 uppercase mb-1">
            Confidence
          </p>
          <ConfidenceBar value={node.confidence_score} />
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <p className="text-[10px] font-mono text-slate-500 uppercase mb-1">
            Instances
          </p>
          <p className="text-lg font-bold text-navy-900">
            {node.instance_count}
          </p>
        </div>
      </div>

      {/* Extraction patterns */}
      {node.extraction_patterns && node.extraction_patterns.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-navy-900 mb-2 flex items-center gap-1.5">
            <Hash size={12} className="text-slate-400" />
            Extraction Patterns
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {node.extraction_patterns.map((p, i) => (
              <span
                key={i}
                className="text-[10px] font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full border border-slate-200"
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Reference ad count */}
      {node.reference_ads && node.reference_ads.length > 0 && (
        <div className="bg-brand-teal/5 rounded-lg border border-brand-teal/20 p-3">
          <p className="text-xs text-brand-teal-dark font-medium flex items-center gap-1.5">
            <Eye size={12} />
            {node.reference_ads.length} reference ad
            {node.reference_ads.length !== 1 ? 's' : ''} linked
          </p>
        </div>
      )}

      {/* Related Creative Formulas */}
      {relatedFormulas.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-navy-900 mb-2 flex items-center gap-1.5">
            <Sparkles size={12} className="text-amber-500" />
            Creative Formulas ({relatedFormulas.length})
          </h4>
          <div className="space-y-2">
            {relatedFormulas.slice(0, 5).map((f, i) => (
              <FormulaCard key={i} formula={f} highlightNode={node.canonical_name} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FormulaCard({
  formula,
  highlightNode,
}: {
  formula: CreativeFormula;
  highlightNode?: string;
}) {
  const steps = [
    { label: 'Hook', value: formula.hook },
    { label: 'Angle', value: formula.angle },
    { label: 'Proof', value: formula.proof },
    { label: 'CTA', value: formula.cta },
  ];

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-navy-900 truncate">
          {formula.name}
        </span>
        <PerfBadge tier={formula.avg_performance_tier} />
      </div>
      <div className="flex items-center gap-1 text-[10px]">
        {steps.map((s, i) => (
          <span key={s.label} className="flex items-center gap-1">
            {i > 0 && (
              <ArrowRight size={8} className="text-slate-300 flex-shrink-0" />
            )}
            <span
              className={clsx(
                'px-1.5 py-0.5 rounded',
                s.value === highlightNode
                  ? 'bg-brand-teal/10 text-brand-teal-dark font-semibold border border-brand-teal/20'
                  : 'bg-slate-50 text-slate-600',
              )}
            >
              {formatCategoryLabel(s.value)}
            </span>
          </span>
        ))}
      </div>
      {formula.emotional_lever && (
        <p className="text-[10px] text-slate-400 mt-1.5">
          Emotional lever: {formatCategoryLabel(formula.emotional_lever)}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reference Ads Panel (Right)
// ---------------------------------------------------------------------------

function AdsPanel() {
  const { selectedNode, filteredAds, adsLoading, taxonomyData } =
    useTaxonomyStore();

  const ads = selectedNode ? filteredAds : (taxonomyData?.reference_ads || []);
  const showAll = !selectedNode;

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-slate-100">
        <h3 className="text-xs font-semibold text-navy-900 flex items-center gap-1.5">
          <Eye size={12} className="text-slate-400" />
          Reference Ads
          {ads.length > 0 && (
            <span className="ml-1 text-[10px] text-slate-400 font-normal">
              ({ads.length})
            </span>
          )}
        </h3>
        {selectedNode && (
          <p className="text-[10px] text-slate-400 mt-0.5 truncate">
            Filtered by: {selectedNode.display_name}
          </p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
        {adsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={18} className="animate-spin text-slate-400" />
          </div>
        ) : ads.length === 0 ? (
          <div className="text-center py-12">
            <Eye size={24} className="mx-auto text-slate-300 mb-2" />
            <p className="text-xs text-slate-400">
              {selectedNode ? 'No ads linked to this node' : 'No reference ads'}
            </p>
          </div>
        ) : (
          ads.slice(0, 50).map((ad) => <AdCard key={ad.ad_id} ad={ad} />)
        )}
      </div>
    </div>
  );
}

function AdCard({ ad }: { ad: ReferenceAd }) {
  const { adImageUrls, selectedClientId, openAdDetail } = useTaxonomyStore();
  const imageUrl = adImageUrls[ad.ad_id];
  const [imgError, setImgError] = useState(false);

  return (
    <div
      onClick={() => openAdDetail(ad.ad_id)}
      className="bg-white rounded-lg border border-slate-200 overflow-hidden hover:shadow-soft hover:border-brand-teal/30 transition-all cursor-pointer"
    >
      {/* Image thumbnail */}
      {imageUrl && !imgError && (
        <div className="w-full h-32 bg-slate-50 overflow-hidden">
          <img
            src={imageUrl}
            alt={ad.title || ad.ad_id}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={() => setImgError(true)}
          />
        </div>
      )}

      <div className="p-3">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <h4 className="text-xs font-medium text-navy-900 line-clamp-2 flex-1">
            {ad.title || ad.description?.slice(0, 60) || ad.ad_id}
          </h4>
          <PerfBadge tier={ad.performance_tier} />
        </div>

        <div className="flex flex-wrap gap-1">
          {ad.platform && (
            <span className="text-[9px] font-mono bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
              {ad.platform.replace('meta_', '')}
            </span>
          )}
          {ad.media_type && (
            <span className="text-[9px] font-mono bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
              {ad.media_type}
            </span>
          )}
          {ad.brand && (
            <span className="text-[9px] font-mono bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
              {ad.brand}
            </span>
          )}
        </div>

        {(ad.primary_hook || ad.primary_angle) && (
          <div className="mt-2 pt-2 border-t border-slate-100 space-y-0.5">
            {ad.primary_hook && (
              <p className="text-[10px] text-slate-500">
                <span className="font-medium text-slate-600">Hook:</span>{' '}
                {formatCategoryLabel(ad.primary_hook)}
              </p>
            )}
            {ad.primary_angle && (
              <p className="text-[10px] text-slate-500">
                <span className="font-medium text-slate-600">Angle:</span>{' '}
                {formatCategoryLabel(ad.primary_angle)}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ad Detail Modal
// ---------------------------------------------------------------------------

function AdDetailModal() {
  const { selectedAdDetail, adDetailLoading, closeAdDetail, adImageUrls, selectNode, taxonomyData } =
    useTaxonomyStore();

  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') closeAdDetail();
    }
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [closeAdDetail]);

  if (!selectedAdDetail && !adDetailLoading) return null;

  const ad = selectedAdDetail?.ad;
  const linkedNodes = selectedAdDetail?.linked_taxonomy_nodes || [];
  const imageUrl = ad ? adImageUrls[ad.ad_id] : undefined;

  const formNodes = linkedNodes.filter((n) => n.dimension === 'form');
  const meaningNodes = linkedNodes.filter((n) => n.dimension === 'meaning');

  const handleNodeClick = (node: LinkedTaxonomyNode) => {
    if (!taxonomyData?.taxonomy_dimensions) return;
    const dims = taxonomyData.taxonomy_dimensions;
    const dimData = dims[node.dimension as keyof typeof dims] as Record<string, TaxonomyNode[]> | undefined;
    if (!dimData) return;
    const catNodes = dimData[node.category];
    const fullNode = catNodes?.find((n: TaxonomyNode) => n.node_id === node.node_id);
    if (fullNode) {
      selectNode(fullNode);
      closeAdDetail();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) closeAdDetail(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-[900px] max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-navy-900">Reference Ad Detail</h2>
          <button
            onClick={closeAdDetail}
            className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        {adDetailLoading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 size={24} className="animate-spin text-slate-400" />
            <span className="ml-3 text-sm text-slate-500">Loading ad details...</span>
          </div>
        ) : ad ? (
          <div className="flex-1 overflow-y-auto">
            {/* Hero: image + meta */}
            <div className="flex gap-6 p-6">
              {/* Image */}
              {imageUrl && (
                <div className="w-64 h-64 rounded-xl overflow-hidden bg-slate-50 border border-slate-200 flex-shrink-0">
                  <img src={imageUrl} alt={ad.title || ad.ad_id} className="w-full h-full object-cover" />
                </div>
              )}

              {/* Meta */}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-navy-900 leading-relaxed mb-3">
                  {ad.description || ad.title}
                </p>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {ad.platform && (
                    <span className="text-[10px] font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                      {ad.platform.replace('meta_', '')}
                    </span>
                  )}
                  {ad.media_type && (
                    <span className="text-[10px] font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                      {ad.media_type}
                    </span>
                  )}
                  {ad.brand && (
                    <span className="text-[10px] font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                      {ad.brand}
                    </span>
                  )}
                  {ad.layout_type && (
                    <span className="text-[10px] font-mono bg-blue-50 text-blue-600 px-2 py-0.5 rounded">
                      {formatCategoryLabel(ad.layout_type)}
                    </span>
                  )}
                </div>

                {/* Taxonomy classification chips */}
                <div className="space-y-1.5">
                  {ad.primary_hook && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-slate-400 w-20">Hook</span>
                      <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded font-medium">
                        {formatCategoryLabel(ad.primary_hook)}
                      </span>
                    </div>
                  )}
                  {ad.primary_angle && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-slate-400 w-20">Angle</span>
                      <span className="bg-sky-50 text-sky-700 px-2 py-0.5 rounded font-medium">
                        {formatCategoryLabel(ad.primary_angle)}
                      </span>
                    </div>
                  )}
                  {ad.primary_proof && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-slate-400 w-20">Proof</span>
                      <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded font-medium">
                        {formatCategoryLabel(ad.primary_proof)}
                      </span>
                    </div>
                  )}
                  {ad.primary_emotional_lever && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-slate-400 w-20">Emotion</span>
                      <span className="bg-rose-50 text-rose-700 px-2 py-0.5 rounded font-medium">
                        {formatCategoryLabel(ad.primary_emotional_lever)}
                      </span>
                    </div>
                  )}
                  {ad.primary_cta && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-slate-400 w-20">CTA</span>
                      <span className="bg-violet-50 text-violet-700 px-2 py-0.5 rounded font-medium">
                        {formatCategoryLabel(ad.primary_cta)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Connected Taxonomy Nodes */}
            {linkedNodes.length > 0 && (
              <div className="px-6 pb-4">
                <h3 className="text-xs font-semibold text-navy-900 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <Layers size={13} className="text-slate-400" />
                  Connected Taxonomy Nodes ({linkedNodes.length})
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {formNodes.length > 0 && (
                    <div>
                      <p className="text-[10px] font-mono text-brand-teal uppercase mb-1.5">Form</p>
                      <div className="space-y-1">
                        {formNodes.map((n) => (
                          <button
                            key={n.node_id}
                            onClick={() => handleNodeClick(n)}
                            className="w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-slate-200 hover:border-brand-teal/30 hover:bg-brand-teal/5 transition-all text-xs"
                          >
                            <Palette size={11} className="text-slate-400 flex-shrink-0" />
                            <span className="truncate text-navy-900">{n.display_name}</span>
                            <span className="text-[10px] text-slate-400 ml-auto flex-shrink-0">
                              {formatCategoryLabel(n.category)}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {meaningNodes.length > 0 && (
                    <div>
                      <p className="text-[10px] font-mono text-purple-600 uppercase mb-1.5">Meaning</p>
                      <div className="space-y-1">
                        {meaningNodes.map((n) => (
                          <button
                            key={n.node_id}
                            onClick={() => handleNodeClick(n)}
                            className="w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-slate-200 hover:border-purple-300 hover:bg-purple-50/50 transition-all text-xs"
                          >
                            <Brain size={11} className="text-slate-400 flex-shrink-0" />
                            <span className="truncate text-navy-900">{n.display_name}</span>
                            <span className="text-[10px] text-slate-400 ml-auto flex-shrink-0">
                              {formatCategoryLabel(n.category)}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Persona Activations */}
            {ad.persona_activations && ad.persona_activations.length > 0 && (
              <div className="px-6 pb-4">
                <h3 className="text-xs font-semibold text-navy-900 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <User size={13} className="text-slate-400" />
                  Persona Activations
                </h3>
                <div className="space-y-2">
                  {ad.persona_activations.map((pa) => (
                    <div key={pa.persona_slug} className="bg-slate-50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-navy-900">{pa.persona_name}</span>
                        <span className={clsx(
                          'text-[10px] font-mono px-1.5 py-0.5 rounded',
                          pa.activation_score >= 0.7
                            ? 'bg-emerald-100 text-emerald-700'
                            : pa.activation_score >= 0.4
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-slate-100 text-slate-500',
                        )}>
                          {Math.round(pa.activation_score * 100)}%
                        </span>
                      </div>
                      {pa.persona_summary && (
                        <p className="text-[10px] text-slate-400 mb-1">{pa.persona_summary}</p>
                      )}
                      <div className="flex flex-wrap gap-1">
                        {pa.reasons.slice(0, 3).map((r, i) => (
                          <span key={i} className="text-[10px] text-slate-500 bg-white px-1.5 py-0.5 rounded border border-slate-200">
                            {r}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Buyer Scenarios */}
            {ad.buyer_scenarios && ad.buyer_scenarios.length > 0 && (
              <div className="px-6 pb-4">
                <h3 className="text-xs font-semibold text-navy-900 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <Target size={13} className="text-slate-400" />
                  Buyer Scenarios
                </h3>
                <div className="space-y-2">
                  {ad.buyer_scenarios.map((bs, i) => (
                    <div key={i} className="bg-slate-50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-navy-900">{bs.name}</span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-mono bg-sky-50 text-sky-600 px-1.5 py-0.5 rounded">
                            {bs.stage}
                          </span>
                          <span className="text-[10px] font-mono text-slate-400">
                            {Math.round(bs.confidence * 100)}%
                          </span>
                        </div>
                      </div>
                      <p className="text-[10px] text-slate-500">{bs.context}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5 italic">{bs.trigger}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Persuasion Summary (from extraction) */}
            {ad.extraction?.persuasion_architecture?.aristotelian_appeals && (
              <div className="px-6 pb-4">
                <h3 className="text-xs font-semibold text-navy-900 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <ShieldCheck size={13} className="text-slate-400" />
                  Persuasion Architecture
                </h3>
                <div className="bg-slate-50 rounded-lg p-3">
                  {(() => {
                    const appeals = ad.extraction!.persuasion_architecture.aristotelian_appeals;
                    return (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-slate-400 w-24">Dominant</span>
                          <span className="font-medium text-navy-900 capitalize">{appeals.dominant_appeal}</span>
                        </div>
                        {['ethos', 'pathos', 'logos'].map((appeal) => {
                          const data = appeals[appeal];
                          if (!data?.present) return null;
                          const mechCount = data.mechanisms?.length || 0;
                          return (
                            <div key={appeal} className="flex items-center gap-2 text-xs">
                              <span className="text-slate-400 w-24 capitalize">{appeal}</span>
                              <div className="flex-1 flex items-center gap-1">
                                <div className="h-1.5 bg-slate-200 rounded-full flex-1 max-w-32">
                                  <div
                                    className={clsx(
                                      'h-full rounded-full',
                                      appeal === 'ethos' ? 'bg-blue-500' : appeal === 'pathos' ? 'bg-rose-500' : 'bg-emerald-500',
                                    )}
                                    style={{ width: `${Math.min(100, mechCount * 25)}%` }}
                                  />
                                </div>
                                <span className="text-[10px] text-slate-400">{mechCount} signals</span>
                              </div>
                            </div>
                          );
                        })}
                        {appeals.appeal_balance && (
                          <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">
                            {appeals.appeal_balance}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Provenance */}
            {ad.provenance && (
              <div className="px-6 pb-6">
                <h3 className="text-xs font-semibold text-navy-900 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <ExternalLink size={13} className="text-slate-400" />
                  Provenance
                </h3>
                <div className="flex flex-wrap gap-1.5 text-[10px]">
                  {ad.provenance.source_pipeline && (
                    <span className="font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                      {ad.provenance.source_pipeline}
                    </span>
                  )}
                  {ad.provenance.extraction_date && (
                    <span className="font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                      {ad.provenance.extraction_date}
                    </span>
                  )}
                  {ad.provenance.platforms?.map((p: string) => (
                    <span key={p} className="font-mono bg-slate-100 text-slate-500 px-2 py-0.5 rounded">
                      {p.toLowerCase()}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stats Header
// ---------------------------------------------------------------------------

function TaxonomyStats() {
  const { taxonomyData } = useTaxonomyStore();
  if (!taxonomyData) return null;

  const dims = taxonomyData.taxonomy_dimensions;
  if (!dims?.form || !dims?.meaning) return null;

  const formCount = Object.values(dims.form).reduce(
    (s, v) => s + v.length,
    0,
  );
  const meaningCount = Object.values(dims.meaning).reduce(
    (s, v) => s + v.length,
    0,
  );

  const stats = [
    {
      label: 'Form Nodes',
      value: formCount,
      color: 'text-indigo-600',
      bg: 'bg-indigo-50',
    },
    {
      label: 'Meaning Nodes',
      value: meaningCount,
      color: 'text-teal-600',
      bg: 'bg-teal-50',
    },
    {
      label: 'Reference Ads',
      value: taxonomyData.reference_ads?.length || 0,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
    },
    {
      label: 'Formulas',
      value: taxonomyData.creative_formulas?.length || 0,
      color: 'text-rose-600',
      bg: 'bg-rose-50',
    },
  ];

  return (
    <div className="grid grid-cols-4 gap-3">
      {stats.map((s) => (
        <div
          key={s.label}
          className="bg-white rounded-xl border border-slate-200 shadow-sm p-4"
        >
          <p className="text-[10px] font-mono text-slate-500 uppercase">
            {s.label}
          </p>
          <p className={clsx('text-xl font-bold mt-1', s.color)}>{s.value}</p>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Client Selector (searchable dropdown)
// ---------------------------------------------------------------------------

function ClientSelector({
  clients,
  selectedClientId,
  onSelect,
  loading,
}: {
  clients: TaxonomyClient[];
  selectedClientId: string | null;
  onSelect: (id: string) => void;
  loading: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = clients.find((c) => c.client_id === selectedClientId);

  const filtered = useMemo(() => {
    if (!query) return clients;
    const q = query.toLowerCase();
    return clients.filter(
      (c) =>
        c.client_brand.toLowerCase().includes(q) ||
        c.client_id.toLowerCase().includes(q) ||
        c.category.toLowerCase().includes(q),
    );
  }, [clients, query]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  return (
    <div ref={containerRef} className="relative w-64">
      <button
        onClick={() => setOpen(!open)}
        disabled={loading}
        className={clsx(
          'w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm transition-all',
          open
            ? 'border-brand-teal ring-2 ring-brand-teal/20'
            : 'border-slate-200 hover:border-slate-300',
          loading && 'opacity-50 cursor-wait',
        )}
      >
        <span className={clsx('truncate', selected ? 'text-navy-900 font-medium' : 'text-slate-400')}>
          {loading ? 'Loading...' : selected ? selected.client_brand : 'Select client'}
        </span>
        {loading ? (
          <Loader2 size={14} className="animate-spin text-slate-400 flex-shrink-0" />
        ) : (
          <ChevronsUpDown size={14} className="text-slate-400 flex-shrink-0" />
        )}
      </button>

      {open && (
        <div className="absolute z-50 top-full mt-1 w-full bg-white rounded-lg border border-slate-200 shadow-lg overflow-hidden">
          {/* Search */}
          <div className="px-3 py-2 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Search size={13} className="text-slate-400 flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search clients..."
                className="w-full text-sm text-navy-900 placeholder:text-slate-400 outline-none bg-transparent"
              />
            </div>
          </div>

          {/* Options */}
          <div className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-xs text-slate-400 text-center">No clients found</p>
            ) : (
              filtered.map((c) => {
                const isSelected = c.client_id === selectedClientId;
                return (
                  <button
                    key={c.client_id}
                    onClick={() => {
                      onSelect(c.client_id);
                      setOpen(false);
                      setQuery('');
                    }}
                    className={clsx(
                      'w-full flex items-center gap-2 px-3 py-2 text-left transition-colors',
                      isSelected
                        ? 'bg-brand-teal/5'
                        : 'hover:bg-slate-50',
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className={clsx(
                            'text-sm truncate',
                            isSelected ? 'text-brand-teal-dark font-medium' : 'text-navy-900',
                          )}
                        >
                          {c.client_brand}
                        </span>
                        {c.reference_ad_count != null && c.reference_ad_count > 0 && (
                          <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded flex-shrink-0">
                            {c.reference_ad_count} ads
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] text-slate-400 truncate block">
                        {c.category}
                      </span>
                    </div>
                    {isSelected && (
                      <Check size={14} className="text-brand-teal flex-shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function TaxonomyPage() {
  const {
    clients,
    selectedClientId,
    taxonomyData,
    taxonomyLoading,
    clientsLoading,
    error,
    fetchClients,
    selectClient,
  } = useTaxonomyStore();

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between gap-6">
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-navy-900">
            Ad Taxonomy Explorer
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Browse form &amp; meaning dimensions, creative formulas, and
            reference ads
          </p>
        </div>

        {/* Client selector */}
        {clients.length > 0 && (
          <ClientSelector
            clients={clients}
            selectedClientId={selectedClientId}
            onSelect={selectClient}
            loading={clientsLoading}
          />
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      {/* Loading */}
      {(clientsLoading || taxonomyLoading) && (
        <div className="flex items-center justify-center py-24">
          <Loader2 size={24} className="animate-spin text-slate-400" />
          <span className="ml-3 text-sm text-slate-500">
            {clientsLoading ? 'Loading clients...' : 'Loading taxonomy...'}
          </span>
        </div>
      )}

      {/* No clients */}
      {!clientsLoading && clients.length === 0 && !error && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-16 text-center">
          <Tags size={36} className="mx-auto text-slate-300 mb-4" />
          <p className="text-sm text-slate-500">
            No ad taxonomy data available yet.
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Run the ad extraction pipeline to generate taxonomy data.
          </p>
        </div>
      )}

      {/* Taxonomy content */}
      {taxonomyData && !taxonomyLoading && taxonomyData.taxonomy_dimensions && (
        <>
          {/* Stats row */}
          <TaxonomyStats />

          {/* Three-panel explorer */}
          <div
            className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex"
            style={{ height: '65vh' }}
          >
            {/* Left: Tree */}
            <div className="w-72 border-r border-slate-200 flex-shrink-0 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                <h3 className="text-xs font-semibold text-navy-900 flex items-center gap-1.5">
                  <Filter size={12} className="text-slate-400" />
                  Taxonomy Tree
                </h3>
              </div>
              <TaxonomyTree />
            </div>

            {/* Center: Detail */}
            <div className="flex-1 min-w-0 border-r border-slate-200 overflow-hidden">
              <NodeDetail />
            </div>

            {/* Right: Ads */}
            <div className="w-80 flex-shrink-0 overflow-hidden">
              <AdsPanel />
            </div>
          </div>
        </>
      )}

      {/* Ad detail modal */}
      <AdDetailModal />
    </div>
  );
}
